import React, { useEffect, useState } from 'react';
import { Form, Space, Grid, notification, theme } from 'antd';
import {
  QuestionType,
  SurveyAnswersResponse,
  SurveyInstanceEntity,
  SurveyFormEntity,
  SurveyInputMode,
  StructuredQuestionFormEntity,
  StructuredQuestionSingleFormEntity,
  QuestionFormEntity,
  UserRole,
  InvitationMode,
  waitingListsKeys,
  WaitingListEntity,
  generateSurveyAnswersValidatorSchema,
} from '@seaters/constants';
import { renderQuestion } from './helpers/renderItems';
import { Button, baseColor300, Show } from '@seaters/ui';
import { FormProvider, useForm } from 'react-hook-form';
import { getInitialValues } from './helpers/getInitialValues';
import { AccessibleRequiredMark } from '@seaters/ui';
import { useFetchAllQuestionsChoices } from './hooks/useFetchAllQuestionsChoices';
import {
  getSingleTranslation,
  useAppUserStore,
  useSendAnswers,
  useSendFanAnswersAsFGO,
  useSendGuestAnswersAsHost,
  useSendGuestAnswers,
  getSlugFromUrl,
  queryClient,
  getSessionToken,
  useSendAnswersAfterEvent,
} from '@seaters/data-access';
import './survey.css';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const { useBreakpoint } = Grid;

const formItemStyle = {
  border: `1px solid ${baseColor300}`,
  borderRadius: '8px',
  padding: '20px',
  marginBottom: '16px',
};
export interface SurveyProps {
  color?: string;
  surveyInstance: SurveyInstanceEntity;
  cancel: () => void;
  surveyAnswers?: SurveyAnswersResponse[];
  previewMode?: boolean;
  //for BEFORE_JOIN_WISH_LIST survey type
  joinWL?: () => void;
  isJoined?: boolean;
  onSuccess?: () => void;
  ticketsAmount?: number;
  code?: string;
  selectedFanId?: string;
  /** Defaults to the route's `waitingListId`; pass it where the route has none. */
  waitingListId?: string;
}

// Enter in a single-line field would submit the whole survey after one answer.
const preventImplicitSubmit = (event: React.KeyboardEvent<HTMLFormElement>) => {
  const target = event.target as HTMLElement;
  if (
    event.key === 'Enter' &&
    target.tagName === 'INPUT' &&
    (target as HTMLInputElement).type !== 'submit'
  ) {
    event.preventDefault();
  }
};

const singleAnswersType = [
  QuestionType.FREE_TEXT,
  QuestionType.CHECKBOX,
  QuestionType.MULTIPLE_CHOICE,
  QuestionType.MULTIPLE_CHOICE_RANGE,
  QuestionType.MULTIPLE_CHOICE_AUTO_COMPLETE,
  QuestionType.MULTIPLE_CHOICE_DROPDOWN,
  QuestionType.MULTIPLE_CHOICE_WITH_OTHER,
  QuestionType.MULTIPLE_CHOICE_WITH_OTHER_AUTO_COMPLETE,
  QuestionType.MULTIPLE_CHOICE_WITH_OTHER_DROPDOWN,
];

const multipleAnswersType = [
  QuestionType.MULTIPLE_CHOICE_AUTO_COMPLETE_MULTIPLEANSWER,
  QuestionType.MULTIPLE_CHOICE_MULTIPLEANSWER,
  QuestionType.MULTIPLE_CHOICE_WITH_OTHER_AUTO_COMPLETE_MULTIPLEANSWER,
];

export function Survey({
  color,
  surveyInstance,
  cancel,
  surveyAnswers,
  joinWL,
  isJoined,
  onSuccess,
  ticketsAmount,
  previewMode,
  code,
  selectedFanId,
  waitingListId: waitingListIdProp,
}: SurveyProps) {
  const { token: themeToken } = theme.useToken();
  const params = useParams();
  const waitingListId = waitingListIdProp ?? params.waitingListId ?? '';
  const { xs } = useBreakpoint();
  const structuredFields =
    surveyInstance.survey?.surveyQuestions
      ?.find((q) => q.question.type === QuestionType.STRUCTURED)
      ?.question?.structure?.map((str) => {
        return { name: str.name, isMandatory: str.mandatory, type: str.type };
      }) ?? [];

  const regularFields =
    surveyInstance.survey?.surveyQuestions
      .filter((q) => q.question.type !== QuestionType.STRUCTURED)
      ?.map((q) => {
        return {
          name: q.question.id,
          isMandatory: q.mandatory,
          type: q.question.type,
        };
      }) ?? [];

  const surveyAnswersSchema = generateSurveyAnswersValidatorSchema(
    regularFields,
    structuredFields
  );

  const structuredQuestionId = surveyInstance.survey?.surveyQuestions?.find(
    (q) => q.question.type === QuestionType.STRUCTURED
  )?.question.id;

  const { t, i18n } = useTranslation();

  const { language } = i18n;
  const lang = language === 'nl_NL' || language === 'nl_BE' ? 'nl' : language;

  const methods = useForm<any>({
    mode: 'onBlur',
    defaultValues: getInitialValues(
      surveyInstance?.survey,
      surveyAnswers,
      ticketsAmount
    ),
    values: getInitialValues(
      surveyInstance?.survey,
      surveyAnswers,
      ticketsAmount
    ),
  });

  const searchParams = new URLSearchParams(window.location.search);

  const token = searchParams.get('token');

  const waitingList = queryClient.getQueryData<WaitingListEntity>(
    waitingListsKeys.detail(waitingListId)
  );

  const { handleSubmit, control, setValue } = methods;

  const [importedData, setImportedData] = useState<{
    data: any[];
    questionId: string;
  }>({ data: [], questionId: '' });
  const { mutate: sendAnswers, isPending: isFanAnswersSending } =
    useSendAnswers(
      surveyInstance.id,
      waitingListId,
      surveyInstance.contextWaitingListId
    );

  const {
    mutate: sendAfterEventAnswers,
    isPending: isAfterEventAnswersSending,
  } = useSendAnswersAfterEvent(
    surveyInstance.id,
    token ?? '',
    surveyInstance.contextWaitingListId
  );

  const { mutate: sendFanAnswersAsFGO, isPending: isFGOAnswersSending } =
    useSendFanAnswersAsFGO(
      surveyInstance.id,
      selectedFanId ?? '',
      waitingListId
    );

  const { mutate: sendGuestAnswersAsHost, isPending: isHostAnswersSending } =
    useSendGuestAnswersAsHost(
      surveyInstance.id,
      selectedFanId ?? '',
      waitingListId
    );

  const { user } = useAppUserStore();

  const { mutate: sendGuestAnswers, isPending: isGuestAnswersSending } =
    useSendGuestAnswers(surveyInstance.id, { code: code ?? '' });

  const isFGO = user?.roles.includes(UserRole.FAN_GROUP_OWNER);
  const isHost = user?.roles.includes(UserRole.HOST);

  const isAnswersSending =
    isFanAnswersSending ||
    isGuestAnswersSending ||
    isAfterEventAnswersSending ||
    isFGOAnswersSending ||
    isHostAnswersSending;

  const isGuest = !!code && !isFGO;

  const isEditable = surveyInstance?.inputMode === SurveyInputMode.EDITABLE;

  const [formDisabled, setFormDisabled] = useState(
    !!surveyAnswers || !!previewMode
  );

  useFetchAllQuestionsChoices(
    surveyInstance.survey.surveyQuestions ?? [],
    isGuest
  );

  // This form appears in Checkout modal for Fan, Fan Group Owner, Guest and  Host.
  // For each role specific mutations should be executed.

  const slug = getSlugFromUrl();
  const isCustom = waitingList?.invitationMode === InvitationMode.ENABLED;
  const auth = getSessionToken();

  const sendAnswersMutation =
    (!auth && !isGuest) || token
      ? sendAfterEventAnswers
      : isGuest
      ? sendGuestAnswers
      : isFGO && selectedFanId && !slug // FGO selects a Fan in rsvp table
      ? sendFanAnswersAsFGO
      : isHost && isCustom && selectedFanId
      ? sendGuestAnswersAsHost
      : sendAnswers;

  const joinAgain = () => {
    joinWL?.();
    cancel();
  };

  const onSubmit = (values: SurveyFormEntity) => {
    // The structured question is shaped differently from every other answer, so
    // it is built on its own rather than as one branch of the same map — that
    // keeps both payload shapes distinct instead of collapsing them to a union.
    const structuredValues = structuredQuestionId
      ? (values[structuredQuestionId] as
          | StructuredQuestionFormEntity
          | undefined)
      : undefined;

    const structuredQuestionAnswer =
      structuredQuestionId && structuredValues
        ? {
            questionId: structuredQuestionId,
            answers: Object.values(structuredValues).map(
              (set: StructuredQuestionSingleFormEntity) => ({
                answerText: null,
                structuredAnswer: set,
              })
            ),
          }
        : undefined;

    const regularQs = Object.keys(values)
      .filter((key) => key !== structuredQuestionId)
      .map((key) => {
        const questionType = surveyInstance.survey.surveyQuestions.filter(
          (question) => question.question.id === key
        )[0].question.type;

        if (singleAnswersType.includes(questionType)) {
          const questionValue = values[key] as QuestionFormEntity;
          const answer = {
            questionId: key,
            answers: [
              questionValue.answerText
                ? { answerText: questionValue.answerText }
                : {
                    offeredAnswerId: questionValue.offeredAnswerId,
                    translatedOfferedAnswer:
                      questionValue.translatedOfferedAnswer,
                  },
            ],
          };
          return answer;
        } else if (multipleAnswersType.includes(questionType)) {
          const questionValue = values[key] as QuestionFormEntity;
          const offeredAnwers =
            typeof questionValue.offeredAnswerId === 'string'
              ? [questionValue.offeredAnswerId]
              : questionValue.offeredAnswerId;

          const answer = {
            questionId: key,
            answers: questionValue.answerText
              ? [{ answerText: questionValue.answerText }]
              : offeredAnwers?.map((item) => {
                  const res = {
                    answerText: null,
                    offeredAnswerId: item,
                    structuredAnswer: [],
                    translatedOfferedAnswer:
                      questionValue.translatedOfferedAnswer,
                  };
                  return res;
                }) ?? [],
          };

          return answer;
        }

        return undefined;
      })
      .filter((answer): answer is NonNullable<typeof answer> => !!answer);

    const parsed = surveyAnswersSchema.safeParse({
      regularAnswers: regularQs.reduce(
        (acc, curr) => ({ ...acc, [curr.questionId]: curr.answers[0] }),
        {}
      ),
      structuredAnswers: structuredQuestionAnswer?.answers[0]?.structuredAnswer,
    });

    if (!parsed.success) {
      console.log(parsed.error?.issues);
      notification.error({
        message: t('survey_answers_validation_error', {
          fields: '(*)',
        }),
      });
      return;
    }

    const answersToSend = structuredQuestionAnswer
      ? [
          ...regularQs,
          {
            ...structuredQuestionAnswer,
            answers: structuredQuestionAnswer.answers.map((ans) => ({
              ...ans,
              structuredAnswer: Object.keys(ans.structuredAnswer).map(
                (key) => ({
                  name: key,
                  value: ans.structuredAnswer[key],
                })
              ),
            })),
          },
        ]
      : regularQs;

    sendAnswersMutation(
      { answers: answersToSend },
      {
        onSuccess: async () => {
          notification.success({
            message: t('survey_answers_sent_notification_success'),
          });
          if (!isGuest && !selectedFanId) {
            cancel();
          }
          if (joinWL && !isJoined) {
            joinWL();
          }
          setFormDisabled(true);

          if (onSuccess) {
            onSuccess();
          }
        },
        onError: (err) => {
          console.error(err);
          notification.error({
            message: t('survey_answers_sent_notification_error'),
            description: t(
              `${err?.response?.data?.errors?.errorsTypes?.validation_errors?.[0]?.error?.errorDescription} (${err?.response?.data?.errors?.errorsTypes?.validation_errors?.[0]?.references?.length})`
            ),
          });
          setFormDisabled(false);
          if (joinWL && !isJoined && !!surveyAnswers?.length) {
            joinWL();
            cancel();
          }
        },
      }
    );
  };

  useEffect(() => {
    importedData.data.forEach((dataItem, i) => {
      Object.keys(dataItem).forEach((key) => {
        if (key !== 'key')
          setValue(
            `${importedData.questionId}.Instance ${i + 1}.${key}`,
            dataItem[key].toString() == 'true'
              ? true
              : dataItem[key].toString() == 'false'
              ? false
              : dataItem[key].toString()
          );
      });
    });
  }, [importedData]);
  return (
    <FormProvider {...methods}>
      <Form
        layout="vertical"
        onFinish={handleSubmit(onSubmit)}
        autoComplete="off"
        disabled={formDisabled || isAnswersSending}
        onKeyDown={preventImplicitSubmit}
        requiredMark={AccessibleRequiredMark}
      >
        {surveyInstance?.survey?.surveyQuestions.map((questionItem, i) => (
          <Form.Item
            style={formItemStyle}
            name={questionItem.question.id}
            required={questionItem.mandatory}
            htmlFor={questionItem.question.id}
            aria-required={questionItem.mandatory}
            label={
              <span data-testid="surveyQuestionLabel">
                {surveyInstance?.survey?.surveyQuestions.length > 1 &&
                  `${i + 1}. `}
                {getSingleTranslation(questionItem.question.text, lang)}
              </span>
            }
          >
            {renderQuestion(
              questionItem,
              setValue,
              control,
              surveyInstance?.extensionPoint,
              setImportedData,
              ticketsAmount,
              lang,
              formDisabled || isAnswersSending,
              themeToken
            )}
          </Form.Item>
        ))}
        <Show
          when={
            !previewMode &&
            (!surveyAnswers || !formDisabled || isAnswersSending)
          }
        >
          <Space
            className={xs ? 'w-full' : undefined}
            direction={xs ? 'vertical' : 'horizontal'}
          >
            <Button
              color={color ? color : undefined}
              type="primary"
              htmlType="submit"
              data-testid="submitSurveyButton"
              loading={isAnswersSending}
              block={xs}
              size={xs ? 'large' : undefined}
            >
              {t('button_survey_answers_send')}
            </Button>
            <Button
              color={color ? color : undefined}
              type="text"
              onClick={cancel}
              data-testid="closeSurveyButton"
              block={xs}
              size={xs ? 'large' : undefined}
            >
              {t('button_text_cancel')}
            </Button>
          </Space>
        </Show>
      </Form>
      <Show
        when={
          !previewMode && surveyAnswers && !isAnswersSending && formDisabled
        }
      >
        <Show
          when={isEditable}
          fallback={
            <Space
              className={xs ? 'w-full' : undefined}
              direction={xs ? 'vertical' : 'horizontal'}
            >
              {joinWL && (
                <Button
                  color={color ? color : undefined}
                  type="primary"
                  onClick={joinAgain}
                  block={xs}
                >
                  {t('queue_header_join-btn')}
                </Button>
              )}
              <Button
                color={color ? color : undefined}
                type="text"
                onClick={cancel}
                block={xs}
              >
                {t('button_text_close')}
              </Button>
            </Space>
          }
        >
          <Space
            className={xs ? 'w-full' : undefined}
            direction={xs ? 'vertical' : 'horizontal'}
            size={xs ? undefined : 24}
          >
            {joinWL && (
              <Button type="primary" onClick={joinAgain} block={xs}>
                {t('queue_header_join-btn')}
              </Button>
            )}
            <Button
              color={color ? color : undefined}
              type="text"
              onClick={() => setFormDisabled(false)}
              block={xs}
            >
              {t('button_survey_answers_edit')}
            </Button>
          </Space>
        </Show>
      </Show>
    </FormProvider>
  );
}

export default Survey;
